import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Button, Grid } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useBlocker } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { flattenDeep, get } from 'lodash';
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useState } from 'react';
import { type ParsedSuperViewData, parseSuperView, type SuperViewResponse } from 'src/api/viewModels/data';
import FieldInput from 'src/components/FieldInput/FieldInput';
import styles from 'src/components/Tabs/InfoTab/InfoTabStyles';
import { createUpdateMetadataMutation } from 'src/dynamoQueries/metadataField';
import { buildSuperViewEditQuery } from 'src/dynamoQueries/superView';
import { exitEditMode } from 'src/helpers';
import { DATE_FIELD_TYPES, normalizeDateValue } from 'src/helpers/dateFields';
import { useCustomToast } from 'src/hooks/menu';

// Extended parsed super view data with additional fields from the query
interface ExtendedSuperViewField extends ParsedSuperViewData {
  slug: string;
  metadataFieldId?: string;
  fieldDefId: string;
  label?: string;
  isEditable?: boolean;
  choices?: string[];
}

interface FormValues {
  [key: string]: string | string[] | undefined | null;
}

interface EditViewProps {
  isTwoColumn: boolean;
  itemId: string;
  viewModel: string;
  formik?: unknown;
  handleCancel?: () => void;
}

const makeInitialValues = (superViewResponse: SuperViewResponse | null | undefined): FormValues => {
  const vals: FormValues = {};
  const superViewData = parseSuperView(superViewResponse, false) as ExtendedSuperViewField[];

  superViewData.forEach((data) => {
    if (data.fieldType === 'MS' || data.fieldType === 'CB') {
      vals[data.slug] = data.value
        ? (data.value as Array<{ data: string }>)
            .map((item) => get(item, 'data'))
            .filter((v): v is string => Boolean(v))
        : [];
      return;
    }
    // Normalize raw date values to compact precision-specific strings so the
    // picker can render/edit without timezone shifts or display-style drift.
    if (DATE_FIELD_TYPES.includes(data.fieldType as (typeof DATE_FIELD_TYPES)[number])) {
      const rawDateValue = data.value?.find((item) => Boolean(item?.data))?.data ?? data.show;
      vals[data.slug] = normalizeDateValue(rawDateValue, data.fieldType);
      return;
    }
    vals[data.slug] = data.show ?? '';
  });

  return vals;
};

/** Compare two field values for equality (handles arrays for MS/CB fields) */
const fieldValuesEqual = (a: FormValues[string], b: FormValues[string]): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return (a ?? '') === (b ?? '');
};

const EditView: React.FC<EditViewProps> = ({ isTwoColumn, itemId, viewModel }) => {
  const classes = styles();
  const client = useApolloClient();
  const { toastr } = useCustomToast();
  const [isSaving, setIsSaving] = useState(false);

  const buildUpdateMetadata = () => {
    const gqlMutation = createUpdateMetadataMutation(true);
    const UMFMutation = jsonToGraphQLQuery(gqlMutation, { pretty: true });
    return gql(UMFMutation);
  };

  const [updateMetadata] = useMutation(buildUpdateMetadata(), {
    fetchPolicy: 'network-only',
  });

  const {
    data: superView,
    loading,
    error,
  } = useQuery<SuperViewResponse>(buildSuperViewEditQuery(), {
    skip: !itemId || !viewModel,
    variables: { globalId: itemId, viewComponentName: viewModel },
    fetchPolicy: 'no-cache',
  });

  const formik = useFormik<FormValues>({
    initialValues: makeInitialValues(superView),
    enableReinitialize: true,
    onSubmit: async (values: FormValues, _helpers: FormikHelpers<FormValues>) => {
      if (isSaving) return;
      setIsSaving(true);
      toastr.info('Saving...');

      const superViewData = parseSuperView(superView, false) as ExtendedSuperViewField[];
      const initialValues = makeInitialValues(superView);

      // Only mutate fields that actually changed
      const dirtyFields = superViewData.filter(({ slug }) => !fieldValuesEqual(values[slug], initialValues[slug]));

      if (dirtyFields.length === 0) {
        toastr.success('No changes to save');
        setIsSaving(false);
        exitEditMode();
        formik.resetForm();
        return;
      }

      const mutations = dirtyFields.map(({ slug, metadataFieldId, fieldDefId }) => {
        const fieldValue = values[slug] ?? '';
        const variables: {
          globalId: string;
          fieldDefId: string;
          valuesList: unknown[];
          fieldId?: string;
        } = {
          globalId: itemId,
          fieldDefId,
          valuesList: flattenDeep([fieldValue]),
        };

        if (metadataFieldId) {
          variables.fieldId = metadataFieldId;
        }

        return updateMetadata({ variables });
      });

      const results = await Promise.allSettled(mutations);
      const failures = results.filter((r) => r.status === 'rejected');

      setIsSaving(false);

      if (failures.length > 0) {
        const failCount = failures.length;
        const totalCount = results.length;
        toastr.error(`${failCount} of ${totalCount} field(s) failed to save`);
        // Stay in edit mode so user can retry
        return;
      }

      toastr.success('Fields updated successfully');

      if (itemId) {
        const updateData = {
          assetId: itemId,
          timestamp: Date.now(),
          processedBy: [] as string[],
        };
        localStorage.setItem('assetUpdated', JSON.stringify(updateData));
      }

      // Evict all cached superView queries so other components (gallery cards,
      // InfoTab, etc.) refetch fresh data instead of showing stale values.
      client.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'superView',
      });
      client.cache.gc();

      exitEditMode();
      formik.resetForm();
    },
  });

  // Track whether we should block navigation (dirty form, not currently saving)
  const shouldBlock = formik.dirty && !isSaving;

  // Clean up edit mode on unmount so navigation handlers don't need to call exitEditMode() directly
  useEffect(() => {
    return () => {
      exitEditMode();
    };
  }, []);

  // Block in-app (SPA) navigation via TanStack Router + browser beforeunload.
  useBlocker({
    shouldBlockFn: () => {
      if (!shouldBlock) return false;
      return !window.confirm('You have unsaved changes. Are you sure you want to leave?');
    },
    withResolver: false,
    enableBeforeUnload: shouldBlock,
  });

  const reinitializeForm = () => {
    formik.setValues(makeInitialValues(superView));
  };

  const handleCancel = () => {
    if (formik.dirty && !window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
      return;
    }
    exitEditMode();
    reinitializeForm();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleCancel is stable, only re-create when formik.dirty changes
  const handleEscKeyPress = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    },
    [formik.dirty],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscKeyPress);
    return () => {
      document.removeEventListener('keydown', handleEscKeyPress);
    };
  }, [handleEscKeyPress]);

  if (loading || Object.keys(formik.values).length === 0) return <CircularProgress color="secondary" />;
  if (error) return <div>Error: {error.message}</div>;

  const superViewData = parseSuperView(superView, false) as ExtendedSuperViewField[];

  const handleFormKeyDown = (event: ReactKeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <form onSubmit={formik.handleSubmit} onKeyDown={handleFormKeyDown}>
      <Grid container={isTwoColumn} rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        {superViewData?.map((field, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: super view fields have no stable unique identifier
          <Grid key={index} item xs={6}>
            <div className={classes.infoBlock}>
              <FieldInput
                viewModel={viewModel}
                field={
                  field as unknown as {
                    slug: string;
                    label?: string;
                    fieldType?: string;
                    isEditable?: boolean;
                    choices?: string[];
                    value?: { data?: string }[] | string | string[];
                  }
                }
                formik={formik}
              />
            </div>
          </Grid>
        ))}
      </Grid>
      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          id="cyp-edit-save-btn"
          className={classes.editButton}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        <Button color="secondary" className={classes.editButton} onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default EditView;
